import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Calendar, MapPin, Car, CreditCard, Plus, XCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomerDashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancellingBooking, setCancellingBooking] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelLoading, setCancelLoading] = useState(false);

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/user/bookings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBookings(response.data);
    } catch (error) {
      console.error("Error fetching bookings:", error);
      toast.error("Failed to load bookings");
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "confirmed": return "bg-green-500";
      case "pending": return "bg-yellow-500";
      case "completed": return "bg-blue-500";
      case "cancelled": return "bg-red-500";
      default: return "bg-gray-500";
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-amber-50">
      {/* Navbar */}
      <nav className="fixed top-0 w-full z-50 backdrop-blur-md bg-white/80 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate("/")}>
              <img 
                src="/elite-cwb-logo.webp" 
                alt="Elite CWB Logo" 
                className="h-24 w-auto" style={{ filter: 'drop-shadow(0 0 10px rgba(212, 175, 55, 0.3))' }}
              />
            </div>
            <div className="flex items-center gap-4">
              <span className="text-gray-700">Welcome, <strong>{user.name}</strong></span>
              <Button 
                data-testid="logout-dashboard-btn"
                variant="outline" 
                onClick={onLogout}
              >
                Logout
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="pt-32 pb-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2">My Bookings</h1>
              <p className="text-gray-600">Manage your chauffeur bookings</p>
            </div>
            <Button 
              data-testid="new-booking-btn"
              onClick={() => navigate("/booking")}
              className="bg-gradient-to-r from-gray-900 to-gray-700 text-white hover:from-gray-800 hover:to-gray-600"
            >
              <Plus className="w-5 h-5 mr-2" />
              New Booking
            </Button>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <p className="text-gray-600">Loading bookings...</p>
            </div>
          ) : bookings.length === 0 ? (
            <Card className="text-center py-12">
              <CardContent>
                <Car className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No bookings yet</h3>
                <p className="text-gray-600 mb-6">Start your luxury journey today</p>
                <Button 
                  data-testid="first-booking-btn"
                  onClick={() => navigate("/booking")}
                  className="bg-gradient-to-r from-gray-900 to-gray-700 text-white hover:from-gray-800 hover:to-gray-600"
                >
                  Make Your First Booking
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-6">
              {bookings.map((booking) => (
                <Card key={booking.id} className="shadow-lg border-2 hover:shadow-xl transition-shadow">
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-2xl">{booking.vehicle_type}</CardTitle>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge className={getStatusColor(booking.status)}>
                            {booking.status.toUpperCase()}
                          </Badge>
                          {booking.deposit_paid && (
                            <Badge className="bg-green-600">Deposit Paid</Badge>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-gray-900">£{booking.total_price}</div>
                        <div className="text-sm text-gray-600">Total</div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-3">
                        <div className="flex items-start gap-3">
                          <MapPin className="w-5 h-5 text-gray-600 mt-0.5" />
                          <div>
                            <div className="text-sm text-gray-600">Pickup</div>
                            <div className="font-semibold">{booking.pickup_location}</div>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <MapPin className="w-5 h-5 text-gray-600 mt-0.5" />
                          <div>
                            <div className="text-sm text-gray-600">Drop-off</div>
                            <div className="font-semibold">{booking.dropoff_location}</div>
                          </div>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div className="flex items-center gap-3">
                          <Calendar className="w-5 h-5 text-gray-600" />
                          <div>
                            <div className="text-sm text-gray-600">Date & Time</div>
                            <div className="font-semibold">{booking.date} at {booking.time}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <CreditCard className="w-5 h-5 text-gray-600" />
                          <div>
                            <div className="text-sm text-gray-600">Deposit (£{booking.deposit_amount})</div>
                            <div className="font-semibold">{booking.deposit_paid ? "Paid" : "Pending"}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                    {!booking.deposit_paid ? (
                      <div className="mt-6 p-4 bg-amber-50 border-2 border-amber-200 rounded-lg">
                        <div className="flex justify-between items-center">
                          <div>
                            <div className="font-semibold text-amber-900">Deposit Payment Required</div>
                            <div className="text-sm text-amber-700">Pay £{booking.deposit_amount} to secure your booking</div>
                            <div className="text-xs text-gray-600 mt-1">Once paid, we'll confirm your booking within 24 hours</div>
                          </div>
                          <Button 
                            data-testid={`pay-deposit-btn-${booking.id}`}
                            onClick={() => window.open(booking.payment_link, '_blank')}
                            className="bg-amber-600 hover:bg-amber-700 text-white"
                          >
                            Pay Now
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-6 p-4 bg-green-50 border-2 border-green-200 rounded-lg">
                        <div className="flex items-center gap-3">
                          <CheckCircle className="w-5 h-5 text-green-600" />
                          <div className="flex-1">
                            <div className="font-semibold text-green-900">Deposit Paid - Booking Confirmed</div>
                            <div className="text-sm text-green-700">Remaining £{booking.total_price - booking.deposit_amount} due on day of service</div>
                          </div>
                          {booking.status === "completed" && (
                            <Button
                              data-testid={`leave-review-btn-${booking.id}`}
                              onClick={() => navigate(`/review/${booking.id}`)}
                              variant="outline"
                              className="border-green-600 text-green-700 hover:bg-green-50"
                            >
                              Leave Review
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerDashboard;